import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Edit, Truck, ChevronDown, ChevronUp, Save, X } from 'lucide-react';
import apiClient from '../../utils/api';
import { toast } from 'sonner';

const BSRateManagement = () => {
  const [bsRates, setBsRates] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBsRate, setEditingBsRate] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingEntryIndex, setEditingEntryIndex] = useState(null);
  const [editingEntryData, setEditingEntryData] = useState(null);

  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    description: '',
    entries: [],
  });

  const [newEntry, setNewEntry] = useState({
    circumference_from: '',
    circumference_to: '',
    rate: '',
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [ratesRes, supRes] = await Promise.all([
        apiClient.get('/bs-rates'),
        apiClient.get('/bs-suppliers'),
      ]);
      setBsRates(ratesRes.data);
      setSuppliers(supRes.data);
    } catch (error) {
      toast.error('Failed to fetch BS rates');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingBsRate(null);
    setFormData({ supplier_id: '', supplier_name: '', description: '', entries: [] });
    setNewEntry({ circumference_from: '', circumference_to: '', rate: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }

    try {
      const sup = suppliers.find(s => s.id === formData.supplier_id);
      const payload = {
        supplier_id: formData.supplier_id,
        supplier_name: sup?.supplier_name || formData.supplier_name || '',
        description: formData.description || null,
        entries: formData.entries,
      };
      if (editingBsRate) {
        await apiClient.put(`/bs-rates/${editingBsRate.id}`, payload);
        toast.success('BS Rate updated successfully');
      } else {
        await apiClient.post('/bs-rates', payload);
        toast.success('BS Rate created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save BS rate');
    }
  };

  const handleEdit = (item) => {
    setEditingBsRate(item);
    setFormData({
      supplier_id: item.supplier_id || '',
      supplier_name: item.supplier_name || '',
      description: item.description || '',
      entries: item.entries || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this BS rate?')) return;
    try {
      await apiClient.delete(`/bs-rates/${id}`);
      toast.success('BS Rate deleted successfully');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete BS rate');
    }
  };

  const addEntry = () => {
    const from = parseFloat(newEntry.circumference_from);
    const to = parseFloat(newEntry.circumference_to);
    const rate = parseFloat(newEntry.rate);
    if (isNaN(from) || isNaN(to) || isNaN(rate)) {
      toast.error('Please fill all fields with valid numbers');
      return;
    }
    if (from >= to) {
      toast.error('Circumference "From" must be less than "To"');
      return;
    }
    setFormData({ ...formData, entries: [...formData.entries, { circumference_from: from, circumference_to: to, rate }] });
    setNewEntry({ circumference_from: '', circumference_to: '', rate: '' });
  };

  const removeEntry = (idx) => {
    setFormData({ ...formData, entries: formData.entries.filter((_, i) => i !== idx) });
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setEditingEntryIndex(null);
    setEditingEntryData(null);
  };

  const startEditEntry = (rateId, idx, entry) => {
    setEditingEntryIndex({ rateId, idx });
    setEditingEntryData({ ...entry });
  };

  const cancelEditEntry = () => {
    setEditingEntryIndex(null);
    setEditingEntryData(null);
  };

  const saveEditEntry = async (item) => {
    if (!editingEntryData || editingEntryIndex === null) return;
    const from = parseFloat(editingEntryData.circumference_from);
    const to = parseFloat(editingEntryData.circumference_to);
    const rate = parseFloat(editingEntryData.rate);
    if (isNaN(from) || isNaN(to) || isNaN(rate)) {
      toast.error('Please fill all fields with valid numbers');
      return;
    }
    if (from >= to) {
      toast.error('Circumference "From" must be less than "To"');
      return;
    }
    try {
      const updated = [...(item.entries || [])];
      updated[editingEntryIndex.idx] = { circumference_from: from, circumference_to: to, rate };
      await apiClient.put(`/bs-rates/${item.id}`, { entries: updated });
      toast.success('Rate entry updated');
      cancelEditEntry();
      fetchAll();
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const deleteEntryInline = async (item, idx) => {
    if (!window.confirm('Delete this rate entry?')) return;
    try {
      const updated = (item.entries || []).filter((_, i) => i !== idx);
      await apiClient.put(`/bs-rates/${item.id}`, { entries: updated });
      toast.success('Rate entry deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Truck className="h-6 w-6 text-slate-700" />
          <h2 className="text-2xl font-primary font-semibold text-slate-900">BS Rate Management</h2>
        </div>
        <Button
          data-testid="add-bs-rate-button"
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          Add BS Rate
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-600">Loading...</div>
      ) : bsRates.length === 0 ? (
        <Card className="p-8 text-center">
          <Truck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">No BS rates found. Create your first BS rate above.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {bsRates.map((item) => (
            <Card key={item.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                onClick={() => toggleExpand(item.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedId === item.id ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                  <div>
                    <h3 className="font-semibold text-slate-900">{item.supplier_name || '-'}</h3>
                    <p className="text-sm text-slate-500">{item.entries?.length || 0} rate entries{item.description ? ` · ${item.description}` : ''}</p>
                  </div>
                </div>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button data-testid={`edit-bs-rate-${item.id}`} onClick={() => handleEdit(item)} variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button data-testid={`delete-bs-rate-${item.id}`} onClick={() => handleDelete(item.id)} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {expandedId === item.id && (
                <div className="border-t border-slate-200 p-4">
                  {item.entries?.length > 0 ? (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th colSpan={2} className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4 border-r border-slate-200">Circumference (mm)</th>
                          <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4">Rate (₹/sq cm)</th>
                          <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4 w-24">Actions</th>
                        </tr>
                        <tr className="border-b border-slate-100">
                          <th className="text-center text-xs font-medium text-slate-400 py-1 px-4">From</th>
                          <th className="text-center text-xs font-medium text-slate-400 py-1 px-4 border-r border-slate-200">To</th>
                          <th></th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.entries.map((entry, idx) => {
                          const isEditing = editingEntryIndex?.rateId === item.id && editingEntryIndex?.idx === idx;
                          return (
                            <tr key={idx} className="border-t border-slate-100">
                              {isEditing ? (
                                <>
                                  <td className="py-2 px-2"><Input type="number" value={editingEntryData?.circumference_from || ''} onChange={(e) => setEditingEntryData({ ...editingEntryData, circumference_from: e.target.value })} className="h-8 text-center text-sm" /></td>
                                  <td className="py-2 px-2 border-r border-slate-200"><Input type="number" value={editingEntryData?.circumference_to || ''} onChange={(e) => setEditingEntryData({ ...editingEntryData, circumference_to: e.target.value })} className="h-8 text-center text-sm" /></td>
                                  <td className="py-2 px-2"><Input type="number" step="0.01" value={editingEntryData?.rate || ''} onChange={(e) => setEditingEntryData({ ...editingEntryData, rate: e.target.value })} className="h-8 text-center text-sm" /></td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex gap-1 justify-center">
                                      <Button variant="ghost" size="sm" onClick={() => saveEditEntry(item)} className="text-green-600 hover:text-green-700 hover:bg-green-50 h-7 w-7 p-0"><Save className="h-4 w-4" /></Button>
                                      <Button variant="ghost" size="sm" onClick={cancelEditEntry} className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-7 w-7 p-0"><X className="h-4 w-4" /></Button>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="py-2 px-4 text-center text-sm text-slate-900">{entry.circumference_from}</td>
                                  <td className="py-2 px-4 text-center text-sm text-slate-900 border-r border-slate-200">{entry.circumference_to}</td>
                                  <td className="py-2 px-4 text-center text-sm font-bold text-slate-900">₹{entry.rate?.toLocaleString()}</td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex gap-1 justify-center">
                                      <Button variant="ghost" size="sm" onClick={() => startEditEntry(item.id, idx, entry)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 w-7 p-0"><Edit className="h-4 w-4" /></Button>
                                      <Button variant="ghost" size="sm" onClick={() => deleteEntryInline(item, idx)} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center text-slate-500 py-4">No rate entries. Click edit to add entries.</p>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="bs-rate-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-primary font-semibold">{editingBsRate ? 'Edit BS Rate' : 'Create New BS Rate'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label>Supplier *</Label>
              <select
                data-testid="bs-rate-supplier-select"
                value={formData.supplier_id}
                onChange={(e) => {
                  const sup = suppliers.find(s => s.id === e.target.value);
                  setFormData({ ...formData, supplier_id: e.target.value, supplier_name: sup?.supplier_name || '' });
                }}
                required
                className="mt-2 h-12 w-full rounded-md border border-slate-200 bg-white px-3 text-base"
              >
                <option value="">-- Select Supplier --</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.supplier_name}</option>)}
              </select>
            </div>

            <div>
              <Label>Description</Label>
              <Input
                data-testid="bs-rate-desc-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional notes"
                className="mt-2 h-12"
              />
            </div>

            <div>
              <Label className="mb-3 block">Rate Entries</Label>
              {formData.entries.length > 0 && (
                <div className="border rounded-lg overflow-hidden mb-4">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase py-2 px-3">From (mm)</th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase py-2 px-3">To (mm)</th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase py-2 px-3">Rate (₹/sq cm)</th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase py-2 px-3 w-16">Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.entries.map((entry, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="py-2 px-3 text-center text-sm">{entry.circumference_from}</td>
                          <td className="py-2 px-3 text-center text-sm">{entry.circumference_to}</td>
                          <td className="py-2 px-3 text-center text-sm font-medium">₹{entry.rate?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-center">
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeEntry(idx)} className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-3">Add Rate Entry</p>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">From (mm)</Label>
                    <Input type="number" step="0.01" value={newEntry.circumference_from} onChange={(e) => setNewEntry({ ...newEntry, circumference_from: e.target.value })} placeholder="100" className="mt-1 h-10" />
                  </div>
                  <div>
                    <Label className="text-xs">To (mm)</Label>
                    <Input type="number" step="0.01" value={newEntry.circumference_to} onChange={(e) => setNewEntry({ ...newEntry, circumference_to: e.target.value })} placeholder="200" className="mt-1 h-10" />
                  </div>
                  <div>
                    <Label className="text-xs">Rate (₹/sq cm)</Label>
                    <Input type="number" step="0.01" value={newEntry.rate} onChange={(e) => setNewEntry({ ...newEntry, rate: e.target.value })} placeholder="0.85" className="mt-1 h-10" />
                  </div>
                  <div className="flex items-end">
                    <Button type="button" variant="outline" onClick={addEntry} className="w-full h-10"><Plus className="h-4 w-4 mr-1" />Add</Button>
                  </div>
                </div>
              </div>
            </div>

            <Button data-testid="submit-bs-rate-button" type="submit" disabled={!formData.supplier_id} className="w-full bg-slate-900 hover:bg-slate-800 h-12">
              {editingBsRate ? 'Update BS Rate' : 'Create BS Rate'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BSRateManagement;
